import React, { useState, useEffect, useRef } from 'react';
import { useInterviewAgent } from '@/hooks/use-interview-agent';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
 Terminal,
 Send,
 Loader2,
 Mic,
 MicOff,
 MessageSquare,
 Monitor,
 User,
 Bot,
 PhoneOff,
 RotateCcw,
} from 'lucide-react';
import {
 AlertDialog,
 AlertDialogAction,
 AlertDialogCancel,
 AlertDialogContent,
 AlertDialogDescription,
 AlertDialogFooter,
 AlertDialogHeader,
 AlertDialogTitle,
 AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import type { InterviewMessage } from '@shared/index';
import { AudioVisualizer } from './AudioVisualizer';
import { Badge } from '@/components/ui/badge';

interface InterviewTerminalProps {
 sessionId: string;
 jobProfileId?: string;
 interviewType?: string;
}

export function InterviewTerminal({
 sessionId,
 jobProfileId,
 interviewType,
}: InterviewTerminalProps) {
 const { user } = useAuth();
 const navigate = useNavigate();
 const audioQueueRef = useRef<ArrayBuffer[]>([]);
 const isPlayingRef = useRef(false);
 const [isSaving, setIsSaving] = useState(false);

 const onMessage = (event: MessageEvent) => {
  if (event.data instanceof Blob) {
   event.data.arrayBuffer().then((buffer) => {
    audioQueueRef.current.push(buffer);
    playNextChunk();
   });
  } else if (event.data instanceof ArrayBuffer) {
   audioQueueRef.current.push(event.data);
   playNextChunk();
  }
 };

 const { agent, state } = useInterviewAgent(sessionId, { onMessage });
 const [inputValue, setInputValue] = useState('');
 const [isRecording, setIsRecording] = useState(false);
 const mediaRecorderRef = useRef<MediaRecorder | null>(null);
 const scrollRef = useRef<HTMLDivElement>(null);

 // Audio Analysis / VAD Refs
 const analyserRef = useRef<AnalyserNode | null>(null);
 const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
 const hasSpokenRef = useRef(false);
 const recorderStreamRef = useRef<MediaStream | null>(null); // Track cloned/compressed stream for proper cleanup

 const messages = state?.messages || [];
 const status = state?.status || 'initializing';
 const [showChat, setShowChat] = useState(true);
 const [stream, setStream] = useState<MediaStream | null>(null);
 const [aiStream, setAiStream] = useState<MediaStream | null>(null);
 const [isAiSpeaking, setIsAiSpeaking] = useState(false);

 // Play next audio chunk from queue
 const playNextChunk = async () => {
  if (isPlayingRef.current || audioQueueRef.current.length === 0) return;

  isPlayingRef.current = true;
  const chunk = audioQueueRef.current.shift();
  if (!chunk) {
   isPlayingRef.current = false;
   return;
  }

  try {
   const audioContext = new (
    window.AudioContext || (window as any).webkitAudioContext
   )();
   const audioBuffer = await audioContext.decodeAudioData(chunk);
   const source = audioContext.createBufferSource();
   source.buffer = audioBuffer;

   // Route for playback
   source.connect(audioContext.destination);

   // Route for visualization
   const destination = audioContext.createMediaStreamDestination();
   source.connect(destination);
   setAiStream(destination.stream);
   setIsAiSpeaking(true);

   source.onended = () => {
    setIsAiSpeaking(false);
    isPlayingRef.current = false;
    playNextChunk();
   };

   source.start(0);
  } catch (err) {
   console.error('Error playing audio chunk:', err);
   isPlayingRef.current = false;
   playNextChunk();
  }
 };

 // Cleanup recorder and stream on UNMOUNT ONLY (not on stream change)
 useEffect(() => {
  return () => {
   console.log('[Cleanup] Component unmounting, cleaning up audio resources');
   if (
    mediaRecorderRef.current &&
    mediaRecorderRef.current.state !== 'inactive'
   ) {
    mediaRecorderRef.current.stop();
   }
   if (recorderStreamRef.current) {
    recorderStreamRef.current.getTracks().forEach((track) => track.stop());
   }
   if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
  };
 }, []); // Empty deps = only on unmount

 // VAD (Voice Activity Detection / Silence Detection) Loop
 useEffect(() => {
  let animationFrame: number;

  const checkSilence = () => {
   if (!isRecording || !analyserRef.current) return;

   const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
   analyserRef.current.getByteFrequencyData(dataArray);

   // Calculate average volume
   const volume = dataArray.reduce((src, a) => src + a, 0) / dataArray.length;

   // Thresholds (tunable)
   const VISIBLE_THRESHOLD = 10; // Noise floor

   if (volume > VISIBLE_THRESHOLD) {
    if (!hasSpokenRef.current) console.log('[VAD] Speech Detected!');
    // User is speaking
    hasSpokenRef.current = true;
    if (silenceTimerRef.current) {
     console.log('[VAD] Silence interrupted, clearing timer.');
     clearTimeout(silenceTimerRef.current);
     silenceTimerRef.current = null;
    }
   } else if (hasSpokenRef.current) {
    // User WAS speaking, now silent. Start timer if not running.
    if (!silenceTimerRef.current && !isAiSpeaking) {
     console.log('[VAD] Silence detected, starting 1.5s timer...');
     silenceTimerRef.current = setTimeout(() => {
      // Confirm silence duration met (e.g., 1.5s)
      console.log('[VAD] Timer fired! Committing audio...');
      agent?.send(JSON.stringify({ type: 'commit_audio' }));
      hasSpokenRef.current = false; // Reset
      silenceTimerRef.current = null;
     }, 1500);
    }
   }

   animationFrame = requestAnimationFrame(checkSilence);
  };

  if (isRecording) {
   checkSilence();
  }

  return () => {
   cancelAnimationFrame(animationFrame);
   if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
  };
 }, [isRecording, isAiSpeaking, agent]);

 // Toggle Microphone recording with Compression
 const toggleMic = async () => {
  if (isRecording) {
   console.log('[Audio] Stopping recording...');
   if (
    mediaRecorderRef.current &&
    mediaRecorderRef.current.state !== 'inactive'
   ) {
    mediaRecorderRef.current.stop();
   }
   setIsRecording(false);
   // Clean up BOTH streams
   if (stream) {
    stream.getTracks().forEach((track) => track.stop());
    setStream(null);
   }
   if (recorderStreamRef.current) {
    recorderStreamRef.current.getTracks().forEach((track) => track.stop());
    recorderStreamRef.current = null;
   }
  } else {
   try {
    const newStream = await navigator.mediaDevices.getUserMedia({
     audio: true,
    });
    setStream(newStream);

    // Audio Context Setup
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(newStream);

    // Compressor Node (Normalization)
    const compressor = audioContext.createDynamicsCompressor();
    compressor.threshold.value = -24;
    compressor.knee.value = 30;
    compressor.ratio.value = 12;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;

    source.connect(compressor);

    // VAD Setup (Analyze Compressed Signal)
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    compressor.connect(analyser); // Source -> Compressor -> Analyser
    analyserRef.current = analyser;

    // Recorder Setup
    const destination = audioContext.createMediaStreamDestination();
    compressor.connect(destination); // Compressor -> Dest
    const recorderStream = destination.stream;
    recorderStreamRef.current = recorderStream; // Store for cleanup

    // Detect supported MIME type
    const mimeTypes = [
     'audio/webm;codecs=opus',
     'audio/webm',
     'audio/mp4',
     'audio/ogg;codecs=opus',
    ];
    let selectedMimeType = '';
    for (const type of mimeTypes) {
     if (MediaRecorder.isTypeSupported(type)) {
      selectedMimeType = type;
      break;
     }
    }
    console.log('[Audio] Selected MIME Type:', selectedMimeType);

    // Use the stream for MediaRecorder
    const mediaRecorder = new MediaRecorder(recorderStream, {
     mimeType: selectedMimeType || undefined, // Fallback to browser default
    });
    console.log('[Audio] MediaRecorder created, state:', mediaRecorder.state);

    mediaRecorder.onstart = () => {
     console.log('[Audio] MediaRecorder STARTED, state:', mediaRecorder.state);
    };

    mediaRecorder.onerror = (e) => {
     console.error('[Audio] MediaRecorder ERROR:', e);
    };

    mediaRecorder.ondataavailable = (event) => {
     // GATING: Do not send user audio if AI is speaking (Echo Cancellation / Interruption prevention)
     if (isAiSpeaking) {
      console.log('[Audio] Gated: AI is speaking.');
      return;
     }

     if (event.data.size > 0 && agent) {
      console.log(`[Audio] Sending chunk: ${event.data.size} bytes`);
      event.data.arrayBuffer().then((buffer) => {
       try {
        agent.send(buffer);
       } catch (err) {
        console.error('[Audio] Failed to send buffer:', err);
       }
      });
     } else {
      if (!agent) console.warn('[Audio] Agent not ready.');
      if (event.data.size === 0) console.warn('[Audio] Empty data chunk.');
     }
    };

    mediaRecorder.onstop = () => {
     console.log('[Audio] MediaRecorder STOPPED');
     // Signal end of audio stream
     agent.send(JSON.stringify({ type: 'commit_audio' }));
    };

    mediaRecorder.start(1000); // Capture chunks every 1000ms
    console.log(
     '[Audio] MediaRecorder.start() called, state after:',
     mediaRecorder.state
    );
    mediaRecorderRef.current = mediaRecorder;
    setIsRecording(true);
   } catch (err) {
    console.error('Failed to access microphone:', err);
    // TODO: Show toast error
   }
  }
 };

 // Auto-scroll to bottom on new messages
 useEffect(() => {
  if (scrollRef.current) {
   scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }
 }, [messages]);

 // Initialize Session on Mount
 const initRef = useRef(false);
 useEffect(() => {
  if (!initRef.current && agent) {
   initRef.current = true;
   // 1. Send Setup Command via WS
   agent.send(
    JSON.stringify({
     type: 'setup',
     payload: {
      userId: user?.id || 'anonymous',
      userName: user?.fullName || 'Candidate',
      jobProfileId,
      interviewType,
      settings: {
       voiceEnabled: false,
       difficulty: 'intermediate',
       focusAreas: [],
      },
     },
    })
   );

   // 2. Send Start Command (slight delay to ensure setup processes or just sequential)
   setTimeout(() => {
    agent.send(JSON.stringify({ type: 'start' }));
   }, 500);
  }
 }, [agent, user, jobProfileId]);

 const handleSend = async (e?: React.FormEvent) => {
  e?.preventDefault();
  if (!inputValue.trim() || status !== 'active') return;

  const text = inputValue.trim();
  setInputValue('');

  try {
   await agent.send(JSON.stringify({ type: 'process_message', payload: text }));
  } catch (err) {
   console.error('Failed to send message:', err);
  }
 };

 const handleEndSession = () => {
  agent.send(JSON.stringify({ type: 'end' }));
  setIsSaving(true);
  setTimeout(() => {
   navigate('/dashboard');
  }, 2000);
 };

 const handleRestart = () => {
  agent.send(JSON.stringify({ type: 'restart' }));
 };

 const handleDiscard = () => {
  navigate('/dashboard');
 };

 if (isSaving) {
  return (
   <div className='flex flex-col items-center justify-center p-12 text-center h-full space-y-4'>
    <Loader2 className='w-12 h-12 text-primary animate-spin' />
    <p className='text-lg font-mono text-primary animate-pulse'>
     SAVING SESSION DATA...
    </p>
   </div>
  );
 }

 if (status === 'initializing' && messages.length === 0) {
  return (
   <div className='flex flex-col items-center justify-center space-y-6 p-12 text-center h-full'>
    <div className='w-20 h-20 bg-primary/10 border border-primary/20 flex items-center justify-center rounded-none'>
     <Terminal className='w-10 h-10 text-primary animate-pulse' />
    </div>
    <div className='max-w-md space-y-4'>
     <h2 className='text-xl font-bold font-mono text-primary uppercase'>
      Initializing System...
     </h2>
     <div className='text-left bg-muted/10 p-4 border border-border/50 font-mono text-xs text-muted-foreground space-y-2'>
      <p>&gt; Connection Established: OK</p>
      <p>&gt; Agent Identity: Verified</p>
      <p className='text-primary animate-pulse'>&gt; Starting session...</p>
     </div>
    </div>
   </div>
  );
 }

 // Determine AI State for visual feedback
 const isAIThinking =
  status === 'active' &&
  messages.length > 0 &&
  messages[messages.length - 1].role === 'user';

 return (
  <div className='flex h-full bg-black/40 border border-border/50 font-mono overflow-hidden relative'>
   {/* Main Stage (Video Area) */}
   <div className='flex-1 flex flex-col p-4 gap-4 overflow-hidden relative'>
    {/* Status Bar */}
    <div className='flex justify-between items-center text-[10px] text-muted-foreground bg-black/20 p-2 rounded border border-border/30'>
     <div className='flex items-center gap-3'>
      <span className='flex items-center gap-1'>
       <span
        className={`w-1.5 h-1.5 rounded-full ${agent.readyState === 1 ? 'bg-green-500' : 'bg-red-500'}`}
       />
       {agent.readyState === 1 ? 'CONNECTED' : 'OFFLINE'}
      </span>
      <span>SESSION: {sessionId.slice(0, 8)}</span>
     </div>
     <div>
      STATUS: <span className='text-primary'>{status.toUpperCase()}</span>
     </div>
    </div>

    {/* Avatars Grid */}
    <div className='flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 min-h-0'>
     {/* AI Interviewer Card */}
     <div className='bg-black/40 border border-border/30 rounded-lg flex flex-col items-center justify-center relative overflow-hidden group'>
      {/* Background Pulse Effect when talking/thinking */}
      <div
       className={`absolute inset-0 bg-primary/5 transition-opacity duration-500 ${isAIThinking ? 'opacity-100 animate-pulse' : 'opacity-0'}`}
      />

      <div className='w-32 h-32 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/20 flex items-center justify-center mb-4 relative z-10 overflow-hidden'>
       <Bot className='w-16 h-16 text-primary/80' />
       {isAIThinking && !isAiSpeaking && (
        <div className='absolute -inset-2 rounded-full border border-primary/30 animate-ping opacity-20' />
       )}
      </div>

      {/* AI Audio Visualizer - Matched to User Position */}
      {isAiSpeaking && (
       <div className='absolute bottom-12 w-32 h-10 z-20'>
        <AudioVisualizer
         stream={aiStream}
         isRecording={true}
         // color='rgb(34, 197, 94)' // Use default gradient for consistency
        />
       </div>
      )}
      <div className='text-center z-10'>
       <h3 className='font-bold text-lg text-primary tracking-tight'>
        AI Interviewer
       </h3>
       <p className='text-xs text-muted-foreground flex items-center justify-center gap-1 mt-1'>
        {isAiSpeaking ? (
         <span className='text-green-500 font-bold'>• Speaking</span>
        ) : isAIThinking ? (
         <>
          <Loader2 className='w-3 h-3 animate-spin' /> Thinking...
         </>
        ) : (
         <span className='text-primary/50'>• Listening</span>
        )}
       </p>
      </div>
      <div className='absolute bottom-4 left-4'>
       <Badge variant='secondary' className='bg-black/60 text-[10px]'>
        HOST
       </Badge>
      </div>
     </div>

     {/* User Card */}
     <div className='bg-black/40 border border-border/30 rounded-lg flex flex-col items-center justify-center relative overflow-hidden'>
      <div className='w-full h-full absolute inset-0 opacity-20'>
       {/* Placeholder for camera pattern or visualizer background */}
       <div className='w-full h-full bg-[radial-gradient(#333_1px,transparent_1px)] [background-size:16px_16px]'></div>
      </div>

      <div className='w-32 h-32 rounded-full bg-muted/10 border-2 border-muted/20 flex items-center justify-center mb-4 relative z-10 overflow-hidden'>
       {user?.email ? (
        // Just using Initials or Icon for now
        <User className='w-16 h-16 text-muted-foreground' />
       ) : (
        <User className='w-16 h-16 text-muted-foreground' />
       )}
      </div>

      {/* Audio Visualizer Overlay */}
      {isRecording && (
       <div className='absolute bottom-12 w-32 h-10 z-20'>
        <AudioVisualizer stream={stream} isRecording={isRecording} />
       </div>
      )}

      <div className='text-center z-10 mt-2'>
       <h3 className='font-bold text-lg text-foreground tracking-tight'>
        {user?.fullName || 'Candidate'}
       </h3>
       <p className='text-xs text-muted-foreground flex items-center justify-center gap-1 mt-1'>
        {isRecording ? (
         isAiSpeaking ? (
          <span className='text-yellow-500'>• Muted (AI Speaking)</span>
         ) : (
          <span className='text-red-500 animate-pulse'>• Live Mic</span>
         )
        ) : (
         <span className='text-muted-foreground/50'>Mic Muted</span>
        )}
       </p>
      </div>

      <div className='absolute bottom-4 left-4 flex gap-2'>
       <Badge variant='secondary' className='bg-black/60 text-[10px]'>
        YOU
       </Badge>
      </div>
     </div>
    </div>

    {/* Bottom Control Bar */}
    <div className='bg-black/60 border border-border/30 rounded-full p-2 flex justify-center items-center gap-4 w-fit mx-auto backdrop-blur-sm'>
     <Button
      onClick={toggleMic}
      variant={isRecording ? 'destructive' : 'secondary'}
      size='icon'
      className='rounded-full h-12 w-12 shadow-lg'
     >
      {isRecording ? (
       <Mic className='w-5 h-5' />
      ) : (
       <MicOff className='w-5 h-5' />
      )}
     </Button>

     <div className='h-8 w-px bg-border/50 mx-2' />

     <Button
      onClick={handleRestart}
      variant='secondary'
      size='icon'
      className='rounded-full h-12 w-12 shadow-lg hover:bg-yellow-500/20 hover:text-yellow-500'
      title='Restart Session'
     >
      <RotateCcw className='w-5 h-5' />
     </Button>

     <div className='h-8 w-px bg-border/50 mx-2' />

     <AlertDialog>
      <AlertDialogTrigger asChild>
       <Button
        variant='destructive'
        size='icon'
        className='rounded-full h-12 w-12 shadow-lg bg-red-600 hover:bg-red-700'
        title='End Interview'
       >
        <PhoneOff className='w-5 h-5' />
       </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className='bg-black/90 border-border/50 text-foreground'>
       <AlertDialogHeader>
        <AlertDialogTitle>End Interview?</AlertDialogTitle>
        <AlertDialogDescription className='text-muted-foreground'>
         Do you want to save the session results or discard them?
        </AlertDialogDescription>
       </AlertDialogHeader>
       <AlertDialogFooter>
        <AlertDialogCancel className='rounded-none border-primary/20 hover:bg-primary/10 hover:text-primary'>
         Cancel
        </AlertDialogCancel>
        <AlertDialogAction
         onClick={handleDiscard}
         className='rounded-none bg-destructive hover:bg-destructive/90 text-white'
        >
         Discard
        </AlertDialogAction>
        <AlertDialogAction
         onClick={handleEndSession}
         className='rounded-none bg-green-600 hover:bg-green-700 text-white'
        >
         Save & End
        </AlertDialogAction>
       </AlertDialogFooter>
      </AlertDialogContent>
     </AlertDialog>

     <div className='h-8 w-px bg-border/50 mx-2' />

     <Button
      variant={showChat ? 'default' : 'ghost'}
      size='icon'
      onClick={() => setShowChat(!showChat)}
      className={`rounded-full h-10 w-10 ${showChat ? 'bg-primary/20 text-primary hover:bg-primary/30' : 'text-muted-foreground'}`}
     >
      <MessageSquare className='w-5 h-5' />
     </Button>
    </div>
   </div>

   {/* Chat Sidebar */}
   <div
    className={`border-l border-border/50 bg-black/40 flex flex-col transition-all duration-300 ease-in-out absolute md:relative right-0 h-full z-20 ${showChat ? 'w-full md:w-80 translate-x-0' : 'w-0 translate-x-full md:translate-x-0 md:w-0 overflow-hidden'}`}
   >
    {/* Chat Header */}
    <div className='p-3 border-b border-border/50 bg-black/40 flex justify-between items-center'>
     <span className='font-mono text-xs font-bold text-muted-foreground'>
      INTERVIEW LOG
     </span>
     <Button
      variant='ghost'
      size='icon'
      className='h-6 w-6 md:hidden'
      onClick={() => setShowChat(false)}
     >
      <Monitor className='w-4 h-4' />
     </Button>
    </div>

    {/* Messages List - Styled for sidebar */}
    <div className='flex-1 overflow-y-auto p-4' ref={scrollRef}>
     <div className='space-y-4'>
      {messages.length === 0 && (
       <div className='text-center text-muted-foreground text-xs py-8'>
        Session initialized.
        <br />
        Waiting for agent...
       </div>
      )}
      {messages.map((msg: InterviewMessage) => (
       <div
        key={msg.id}
        className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
       >
        <div
         className={`px-3 py-2 text-xs rounded-lg max-w-[90%] border ${
          msg.role === 'user'
           ? 'bg-primary/10 border-primary/30 text-primary rounded-tr-none'
           : 'bg-muted/10 border-border/50 text-foreground rounded-tl-none'
         }`}
        >
         {msg.content}
        </div>
        <span className='text-[8px] text-muted-foreground mt-1 opacity-50'>
         {new Date(msg.timestamp).toLocaleTimeString([], {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
         })}
        </span>
       </div>
      ))}
      {/* Typing Indicator */}
      {isAIThinking && (
       <div className='flex items-center gap-2 text-muted-foreground text-xs pl-2'>
        <Loader2 className='w-3 h-3 animate-spin' />
        <span>Agent is thinking...</span>
       </div>
      )}
     </div>
    </div>

    {/* Input Area - Compact for sidebar */}
    <div className='p-3 border-t border-border/50 bg-black/20'>
     <form onSubmit={handleSend} className='flex gap-2'>
      <Input
       value={inputValue}
       onChange={(e) => setInputValue(e.target.value)}
       placeholder='Type a message...'
       disabled={status !== 'active'}
       className='bg-black/50 border-border/50 h-9 text-xs focus-visible:ring-primary/30'
      />
      <Button
       type='submit'
       disabled={status !== 'active' || !inputValue.trim()}
       size='icon'
       className='h-9 w-9 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/50'
      >
       <Send className='w-4 h-4' />
      </Button>
     </form>
    </div>
   </div>
  </div>
 );
}
